import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Eye, Stamp, Globe, Archive, LogOut, X, Ban } from "lucide-react";
import EditionsTab from "@/pages/admin/EditionsTab";

const STATUS_STYLE = {
  DRAFT: "text-hussar-slate border-white/15",
  CORRECTION_STAGED: "text-amber-400 border-amber-400/30",
  PUBLISHED: "text-emerald-400 border-emerald-400/30",
  UNPUBLISHED: "text-red-400 border-red-400/30",
};

const EMPTY_FORM = {
  title: "", dek: "", body: "", category: "Weekly Intelligence",
  confidence_label: "EXPLORATORY", learning_report_id: "", research_period: "", evidence_cutoff: "",
};

function Badge({ text, className }) {
  return <span className={`font-mono text-[10px] uppercase tracking-[0.18em] border px-2.5 py-1 ${className}`}>{text}</span>;
}

function ArticleEditor({ article, onClose, onSaved }) {
  const isNew = !article?.article_id;
  const [form, setForm] = useState(isNew ? EMPTY_FORM : {
    title: article.title, dek: article.dek || "", body: article.body || "", category: article.category,
    confidence_label: article.confidence_label, learning_report_id: article.learning_report_id || "",
    research_period: article.research_period || "", evidence_cutoff: article.evidence_cutoff || "",
  });
  const [current, setCurrent] = useState(article || null);
  const [changeNote, setChangeNote] = useState("");
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const run = async (label, fn) => {
    setBusy(label);
    setError("");
    try {
      const doc = await fn();
      if (doc) setCurrent(doc);
      onSaved();
      return doc;
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      return null;
    } finally {
      setBusy("");
    }
  };

  const save = () => run("save", async () => {
    if (isNew && !current) {
      const r = await api.post("/admin/articles", form);
      toast.success("Draft created");
      return r.data;
    }
    const r = await api.put(`/admin/articles/${current.article_id}`, form);
    toast.success(r.data.status === "CORRECTION_STAGED" ? "Saved — staged as a correction requiring fresh approval" : "Draft saved");
    return r.data;
  });

  const approve = (approved) => run("approve", async () => {
    const r = await api.post(`/admin/articles/${current.article_id}/approve`, {
      owner_approved: approved,
      sanitizer_status: approved ? "SANITIZED" : "PENDING",
    });
    toast.success(approved ? "Owner approval granted, sanitizer passed" : "Approval revoked");
    return r.data;
  });

  const publish = () => run("publish", async () => {
    const r = await api.post(`/admin/articles/${current.article_id}/publish`, { change_note: changeNote });
    toast.success(`Published as v${r.data.current_version}`);
    setChangeNote("");
    return r.data;
  });

  const unpublish = () => run("unpublish", async () => {
    const r = await api.post(`/admin/articles/${current.article_id}/unpublish`);
    toast.success("Unpublished — historical record preserved");
    return r.data;
  });

  const status = current?.status || "DRAFT";
  const canPublish = current && current.owner_approved && current.sanitizer_status === "SANITIZED";
  const needsNote = (current?.current_version || 0) >= 1;

  return (
    <div data-testid="article-editor" className="fixed inset-0 z-50 bg-hussar-bg/95 backdrop-blur-sm overflow-y-auto">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <h2 className="font-serif text-2xl text-hussar-ink">{isNew && !current ? "New Draft" : "Edit Edition"}</h2>
            {current && <Badge text={status} className={STATUS_STYLE[status]} />}
          </div>
          <button onClick={onClose} data-testid="editor-close-btn" className="text-hussar-muted hover:text-hussar-ink p-2"><X size={20} /></button>
        </div>

        {/* Workflow bar */}
        {current && (
          <div className="border border-white/10 bg-hussar-card p-5 mb-8 flex flex-wrap items-center gap-4" data-testid="workflow-bar">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em]">
              <span className="text-hussar-muted">Sanitizer:</span>
              <span className={current.sanitizer_status === "SANITIZED" ? "text-emerald-400" : "text-hussar-muted"} data-testid="sanitizer-status">{current.sanitizer_status}</span>
            </div>
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em]">
              <span className="text-hussar-muted">Owner approval:</span>
              <span className={current.owner_approved ? "text-emerald-400" : "text-hussar-muted"} data-testid="approval-status">{current.owner_approved ? "GRANTED" : "REQUIRED"}</span>
            </div>
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em]">
              <span className="text-hussar-muted">Version:</span>
              <span className="text-hussar-gold" data-testid="version-status">v{current.current_version}</span>
            </div>
            <div className="flex-1" />
            {!current.owner_approved ? (
              <button onClick={() => approve(true)} disabled={!!busy} data-testid="approve-btn" className="btn-ghost !px-4 !py-2">
                <Stamp size={13} /> Sanitize & Approve
              </button>
            ) : (
              <button onClick={() => approve(false)} disabled={!!busy} data-testid="revoke-approval-btn" className="btn-ghost !px-4 !py-2 !border-red-400/30 !text-red-400">
                Revoke Approval
              </button>
            )}
            {status !== "UNPUBLISHED" && (
              <button onClick={publish} disabled={!!busy || !canPublish} data-testid="publish-btn" className="btn-gold !px-4 !py-2 disabled:opacity-40">
                <Globe size={13} /> {needsNote ? "Publish Correction" : "Publish"}
              </button>
            )}
            {(status === "PUBLISHED" || status === "CORRECTION_STAGED") && (
              <button onClick={unpublish} disabled={!!busy} data-testid="unpublish-btn" className="btn-ghost !px-4 !py-2 !border-red-400/30 !text-red-400">
                <Archive size={13} /> Unpublish
              </button>
            )}
          </div>
        )}

        {needsNote && current && status !== "UNPUBLISHED" && (
          <div className="mb-8">
            <label className="metadata-label !text-[10px] block mb-2">Correction Change Note (required for v{current.current_version + 1})</label>
            <input value={changeNote} onChange={(e) => setChangeNote(e.target.value)} data-testid="change-note-input" className="field-input" placeholder="What changed and why — becomes part of the permanent record" />
          </div>
        )}

        <div className="flex gap-3 mb-6">
          <button onClick={() => setPreview(false)} data-testid="editor-tab-write" className={`font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2 border ${!preview ? "border-hussar-gold text-hussar-gold" : "border-white/10 text-hussar-muted"}`}>Write</button>
          <button onClick={() => setPreview(true)} data-testid="editor-tab-preview" className={`font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2 border ${preview ? "border-hussar-gold text-hussar-gold" : "border-white/10 text-hussar-muted"}`}><Eye size={12} className="inline mr-1" /> Preview</button>
        </div>

        {!preview ? (
          <div className="space-y-5">
            <div>
              <label className="metadata-label !text-[10px] block mb-2">Title *</label>
              <input value={form.title} onChange={set("title")} data-testid="field-title" className="field-input !text-lg font-serif" placeholder="Edition title" />
            </div>
            <div>
              <label className="metadata-label !text-[10px] block mb-2">Dek / Summary</label>
              <textarea value={form.dek} onChange={set("dek")} rows={2} data-testid="field-dek" className="field-input resize-none" placeholder="One-paragraph editorial summary" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="metadata-label !text-[10px] block mb-2">Category</label>
                <input value={form.category} onChange={set("category")} data-testid="field-category" className="field-input" />
              </div>
              <div>
                <label className="metadata-label !text-[10px] block mb-2">Research Confidence</label>
                <select value={form.confidence_label} onChange={set("confidence_label")} data-testid="field-confidence" className="field-input">
                  <option value="HIGH">HIGH</option>
                  <option value="MODERATE">MODERATE</option>
                  <option value="EXPLORATORY">EXPLORATORY</option>
                </select>
              </div>
              <div>
                <label className="metadata-label !text-[10px] block mb-2">Source Learning Report ID</label>
                <input value={form.learning_report_id} onChange={set("learning_report_id")} data-testid="field-learning-report-id" className="field-input font-mono" placeholder="HLR-2026-W28" />
              </div>
              <div>
                <label className="metadata-label !text-[10px] block mb-2">Research Period</label>
                <input value={form.research_period} onChange={set("research_period")} data-testid="field-research-period" className="field-input" placeholder="06 Jul 2026 — 12 Jul 2026" />
              </div>
              <div>
                <label className="metadata-label !text-[10px] block mb-2">Evidence Cutoff</label>
                <input value={form.evidence_cutoff} onChange={set("evidence_cutoff")} data-testid="field-evidence-cutoff" className="field-input" placeholder="12 Jul 2026" />
              </div>
            </div>
            <div>
              <label className="metadata-label !text-[10px] block mb-2">Body — use ## headings, &gt; callouts, blank lines between paragraphs</label>
              <textarea value={form.body} onChange={set("body")} rows={16} data-testid="field-body" className="field-input resize-y font-mono !text-xs leading-relaxed" placeholder="The sanitized weekly finding…" />
            </div>
            <button onClick={save} disabled={!!busy || !form.title.trim()} data-testid="save-draft-btn" className="btn-gold disabled:opacity-40">
              {busy === "save" ? "Saving…" : isNew && !current ? "Create Draft" : "Save Changes"}
            </button>
          </div>
        ) : (
          <div className="border border-white/10 bg-hussar-card p-10" data-testid="editor-preview">
            <div className="metadata-label mb-4">{form.category}</div>
            <h1 className="font-serif text-3xl sm:text-4xl text-hussar-ink mb-6">{form.title || "Untitled"}</h1>
            <p className="font-serif italic text-hussar-slate text-lg mb-8">{form.dek}</p>
            <div className="gold-hairline mb-8" />
            <div className="article-body">
              {(form.body || "").split(/\n\n+/).map((b, i) => {
                const t = b.trim();
                if (!t) return null;
                if (t.startsWith("## ")) return <h2 key={i}>{t.slice(3)}</h2>;
                if (t.startsWith("### ")) return <h3 key={i}>{t.slice(4)}</h3>;
                if (t.startsWith("> ")) return <blockquote key={i}>{t.slice(2)}</blockquote>;
                const lines = t.split("\n").map((l) => l.trim()).filter(Boolean);
                if (lines.length > 1 && lines.every((l) => l.startsWith("- "))) {
                  return <ul key={i}>{lines.map((l, j) => <li key={j}>{l.slice(2)}</li>)}</ul>;
                }
                return <p key={i}>{t}</p>;
              })}
            </div>
          </div>
        )}

        {error && <p data-testid="editor-error" className="text-red-400 text-xs font-mono mt-6">{error}</p>}

        {current?.versions?.length > 0 && (
          <div className="mt-12 border-t border-white/10 pt-8" data-testid="editor-version-history">
            <div className="metadata-label !text-[10px] mb-4">Published Version History</div>
            {[...current.versions].reverse().map((v) => (
              <div key={v.version} className="mb-3 text-xs">
                <span className="text-hussar-gold font-mono">v{v.version}</span>
                <span className="text-hussar-muted ml-3">{new Date(v.published_at).toLocaleString("en-GB")}</span>
                {v.change_note && <span className="text-hussar-slate ml-3 font-light">{v.change_note}</span>}
              </div>
            ))}
          </div>
        )}

        {current?.events?.length > 0 && (
          <div className="mt-8 border-t border-white/10 pt-8" data-testid="editor-event-log">
            <div className="metadata-label !text-[10px] mb-4">Event Log</div>
            {[...current.events].reverse().map((ev, i) => (
              <div key={i} className="mb-2 text-xs font-mono">
                <span className="text-hussar-gold">{ev.event}</span>
                <span className="text-hussar-muted ml-3">{new Date(ev.at).toLocaleString("en-GB")}</span>
                {ev.note && <span className="text-hussar-muted ml-3">{ev.note}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ArticlesTab() {
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | article

  const load = useCallback(() => {
    api.get("/admin/articles").then((r) => setArticles(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="articles-tab">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-serif text-2xl text-hussar-ink">Weekly Intelligence Editions</h2>
        <button onClick={() => setEditing("new")} data-testid="new-draft-btn" className="btn-gold !px-5 !py-2.5">
          <Plus size={13} /> New Draft
        </button>
      </div>
      {articles.length === 0 ? (
        <div className="border border-white/5 bg-hussar-card p-12 text-center text-hussar-muted font-mono text-xs uppercase tracking-widest">
          No editions yet. Create the first draft.
        </div>
      ) : (
        <div className="border border-white/5">
          {articles.map((a) => (
            <button
              key={a.article_id}
              onClick={() => setEditing(a)}
              data-testid={`article-item-${a.slug}`}
              className="w-full text-left grid grid-cols-12 gap-4 items-center px-6 py-5 border-b border-white/5 bg-hussar-card hover:bg-hussar-bg2 transition-colors"
            >
              <div className="col-span-6">
                <div className="text-hussar-ink font-serif">{a.title}</div>
                <div className="text-hussar-muted text-xs font-mono mt-1">{a.slug} · v{a.current_version}</div>
              </div>
              <div className="col-span-2"><Badge text={a.confidence_label} className="text-hussar-gold border-hussar-gold/30" /></div>
              <div className="col-span-2"><Badge text={a.status} className={STATUS_STYLE[a.status]} /></div>
              <div className="col-span-2 text-right text-hussar-muted text-xs font-mono">
                {a.owner_approved ? "APPROVED" : "NO APPROVAL"}
              </div>
            </button>
          ))}
        </div>
      )}
      {editing && (
        <ArticleEditor
          article={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}

function SubscribersTab() {
  const [subs, setSubs] = useState([]);
  const load = useCallback(() => { api.get("/admin/subscribers").then((r) => setSubs(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const suppress = async (email) => {
    await api.post("/admin/subscribers/suppress", { email });
    toast.success(`${email} added to suppression list`);
    load();
  };

  return (
    <div data-testid="subscribers-tab">
      <h2 className="font-serif text-2xl text-hussar-ink mb-2">Newsletter Subscribers</h2>
      <p className="text-hussar-muted text-xs font-mono uppercase tracking-[0.15em] mb-8">{subs.filter((s) => s.status === "active").length} active · never exposed publicly</p>
      {subs.length === 0 ? (
        <div className="border border-white/5 bg-hussar-card p-12 text-center text-hussar-muted font-mono text-xs uppercase tracking-widest">No subscribers yet.</div>
      ) : (
        <div className="border border-white/5">
          {subs.map((s) => (
            <div key={s.email} className="grid grid-cols-12 gap-4 items-center px-6 py-4 border-b border-white/5 bg-hussar-card" data-testid={`subscriber-row-${s.email}`}>
              <div className="col-span-5 text-hussar-ink text-sm font-mono">{s.email}</div>
              <div className="col-span-2"><Badge text={s.status} className={s.status === "active" ? "text-emerald-400 border-emerald-400/30" : "text-hussar-muted border-white/10"} /></div>
              <div className="col-span-3 text-hussar-muted text-xs font-mono">{s.source} · {s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString("en-GB") : ""}</div>
              <div className="col-span-2 text-right">
                {s.status === "active" && (
                  <button onClick={() => suppress(s.email)} data-testid={`suppress-${s.email}`} className="text-red-400/70 hover:text-red-400 text-xs font-mono uppercase tracking-widest">
                    <Ban size={12} className="inline mr-1" /> Suppress
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AccessRequestsTab() {
  const [reqs, setReqs] = useState([]);
  useEffect(() => { api.get("/admin/access-requests").then((r) => setReqs(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="access-requests-tab">
      <h2 className="font-serif text-2xl text-hussar-ink mb-8">Access Requests</h2>
      {reqs.length === 0 ? (
        <div className="border border-white/5 bg-hussar-card p-12 text-center text-hussar-muted font-mono text-xs uppercase tracking-widest">No requests yet.</div>
      ) : (
        <div className="border border-white/5">
          {reqs.map((r) => (
            <div key={r.request_id} className="px-6 py-5 border-b border-white/5 bg-hussar-card" data-testid={`access-request-${r.request_id}`}>
              <div className="flex flex-wrap items-center gap-4 mb-2">
                <span className="text-hussar-ink font-serif text-lg">{r.name}</span>
                <Badge text={r.intended_use} className="text-hussar-gold border-hussar-gold/30" />
                <span className="text-hussar-muted text-xs font-mono">{new Date(r.created_at).toLocaleString("en-GB")}</span>
              </div>
              <div className="text-hussar-slate text-sm font-mono">{r.email}{r.organization ? ` · ${r.organization}` : ""}{r.role ? ` · ${r.role}` : ""}</div>
              {r.message && <p className="text-hussar-muted text-sm font-light mt-2">{r.message}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminConsole() {
  const [auth, setAuth] = useState(null); // null=checking, false=denied, user=ok
  const [tab, setTab] = useState("articles");
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Publishing Console | Hussar Treasury";
    api.get("/auth/me").then((r) => setAuth(r.data)).catch(() => setAuth(false));
  }, []);

  useEffect(() => {
    if (auth === false) navigate("/admin/login");
  }, [auth, navigate]);

  const logout = async () => {
    await api.post("/auth/logout");
    navigate("/admin/login");
  };

  if (!auth) {
    return <div className="min-h-screen bg-hussar-bg flex items-center justify-center text-hussar-muted font-mono text-xs uppercase tracking-widest">Verifying access…</div>;
  }

  const TABS = [["articles", "Editions"], ["newsletter", "Newsletter"], ["subscribers", "Subscribers"], ["requests", "Access Requests"]];

  return (
    <div data-testid="admin-console" className="min-h-screen bg-hussar-bg text-hussar-ink">
      <div className="grain-overlay" />
      <header className="border-b border-white/5 glass-panel sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 border border-hussar-gold/50 flex items-center justify-center">
              <span className="font-serif text-hussar-gold text-sm">H</span>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-hussar-muted">Publishing Console</span>
          </div>
          <div className="flex items-center gap-6">
            <span className="text-hussar-muted text-xs font-mono hidden sm:block">{auth.email}</span>
            <button onClick={logout} data-testid="admin-logout-btn" className="text-hussar-muted hover:text-red-400 text-xs font-mono uppercase tracking-widest transition-colors">
              <LogOut size={12} className="inline mr-1" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex gap-3 mb-10" data-testid="admin-tabs">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`tab-${key}`}
              className={`font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 border transition-colors ${
                tab === key ? "border-hussar-gold text-hussar-gold bg-hussar-gold/5" : "border-white/10 text-hussar-muted hover:text-hussar-slate"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {tab === "articles" && <ArticlesTab />}
        {tab === "newsletter" && <EditionsTab />}
        {tab === "subscribers" && <SubscribersTab />}
        {tab === "requests" && <AccessRequestsTab />}
      </div>
    </div>
  );
}
